import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Chip,
  alpha,
  useTheme,
  Collapse,
  Button,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Phone,
  Mail,
  RotateCcw,
  Monitor,
  Calendar,
  MoreHorizontal,
} from "lucide-react";
import {
  createOrgTask,
  updateOrgTask,
  deleteOrgTask,
} from "../../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";

const TASK_TYPES = [
  { value: "call", label: "Appel", icon: Phone, color: "#3b82f6" },
  { value: "email", label: "Email", icon: Mail, color: "#8b5cf6" },
  { value: "follow_up", label: "Relance", icon: RotateCcw, color: "#f59e0b" },
  { value: "demo", label: "Démo", icon: Monitor, color: "#10b981" },
  { value: "meeting", label: "Réunion", icon: Calendar, color: "#6366f1" },
  { value: "other", label: "Autre", icon: MoreHorizontal, color: "#6b7280" },
];

const FicheTasks = ({ orgId, tasks }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", type: "call", due_date: "" });

  const now = new Date();

  const getTaskStyle = (task) => {
    if (task.status === "done") return { color: theme.palette.text.disabled, deco: "line-through" };
    const due = new Date(task.due_date);
    if (due < now) return { color: "#ef4444", deco: "none" }; // overdue
    if (due.toDateString() === now.toDateString()) return { color: "#f59e0b", deco: "none" }; // today
    return { color: theme.palette.text.primary, deco: "none" };
  };

  const handleToggleDone = async (task) => {
    const newStatus = task.status === "done" ? "pending" : "done";
    try {
      await dispatch(updateOrgTask(orgId, task.id, { status: newStatus }));
    } catch {
      enqueueSnackbar("Erreur", { variant: "error" });
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await dispatch(deleteOrgTask(orgId, taskId));
    } catch {
      enqueueSnackbar("Erreur suppression", { variant: "error" });
    }
  };

  const handleCreate = async () => {
    if (!newTask.title.trim() || !newTask.due_date) {
      enqueueSnackbar("Titre et date requis", { variant: "warning" });
      return;
    }
    try {
      await dispatch(createOrgTask(orgId, {
        ...newTask,
        due_date: new Date(newTask.due_date).toISOString(),
      }));
      setNewTask({ title: "", type: "call", due_date: "" });
      setShowForm(false);
      enqueueSnackbar("Tâche créée", { variant: "success" });
    } catch {
      enqueueSnackbar("Erreur création", { variant: "error" });
    }
  };

  const pendingTasks = tasks.filter((t) => t.status !== "done");
  const doneTasks = tasks.filter((t) => t.status === "done");
  const sortedTasks = [...pendingTasks, ...doneTasks];

  const formatDate = (d) => {
    const date = new Date(d);
    const diff = Math.ceil((date - now) / 86400000);
    if (diff < 0) return `${Math.abs(diff)}j en retard`;
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return "Demain";
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1,
        }}
      >
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", flex: 1, "&:hover": { opacity: 0.8 } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
            Tâches
          </Typography>
          <Chip label={pendingTasks.length} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700 }} />
          {expanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
        </Box>
        <IconButton size="small" onClick={() => { setExpanded(true); setShowForm(true); }} sx={{ color: theme.palette.primary.main }}>
          <Plus size={16} />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2 }}>
          {/* Add task form */}
          <Collapse in={showForm}>
            <Box
              sx={{
                p: 1.5, mb: 1, borderRadius: "8px",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                display: "flex", flexDirection: "column", gap: 1,
              }}
            >
              <TextField
                fullWidth size="small" placeholder="Titre de la tâche..."
                value={newTask.title}
                onChange={(e) => setNewTask((p) => ({ ...p, title: e.target.value }))}
                sx={{ "& input": { fontSize: "0.8rem" } }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Select
                  value={newTask.type}
                  onChange={(e) => setNewTask((p) => ({ ...p, type: e.target.value }))}
                  size="small"
                  sx={{ flex: 1, fontSize: "0.75rem" }}
                >
                  {TASK_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value} sx={{ fontSize: "0.75rem" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <t.icon size={12} color={t.color} />
                        {t.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <TextField
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask((p) => ({ ...p, due_date: e.target.value }))}
                  size="small"
                  sx={{ flex: 1, "& input": { fontSize: "0.75rem" } }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button size="small" onClick={() => setShowForm(false)} sx={{ textTransform: "none", fontSize: "0.7rem" }}>
                  Annuler
                </Button>
                <Button size="small" variant="contained" onClick={handleCreate} sx={{ textTransform: "none", fontSize: "0.7rem" }}>
                  Ajouter
                </Button>
              </Box>
            </Box>
          </Collapse>

          {/* Task list */}
          {sortedTasks.length === 0 ? (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, textAlign: "center", display: "block", py: 1 }}>
              Aucune tâche
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {sortedTasks.map((task) => {
                const typeConfig = TASK_TYPES.find((t) => t.value === task.type) || TASK_TYPES[5];
                const style = getTaskStyle(task);
                return (
                  <Box
                    key={task.id}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      py: 0.5, px: 0.5, borderRadius: "6px",
                      "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                      "&:hover .task-delete": { opacity: 1 },
                    }}
                  >
                    <Checkbox
                      checked={task.status === "done"}
                      onChange={() => handleToggleDone(task)}
                      size="small"
                      sx={{ p: 0.25, "& .MuiSvgIcon-root": { fontSize: 16 } }}
                    />
                    <Box sx={{ width: 18, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                      <typeConfig.icon size={11} color={task.status === "done" ? theme.palette.text.disabled : typeConfig.color} />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1, fontSize: "0.75rem",
                        textDecoration: style.deco,
                        color: style.color,
                      }}
                      noWrap
                    >
                      {task.title}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: "0.6rem", color: style.color, flexShrink: 0, fontWeight: 500 }}>
                      {formatDate(task.due_date)}
                    </Typography>
                    <IconButton
                      className="task-delete"
                      size="small"
                      onClick={() => handleDelete(task.id)}
                      sx={{ p: 0.25, opacity: 0, transition: "opacity 0.15s", color: theme.palette.text.disabled, "&:hover": { color: "#ef4444" } }}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FicheTasks;
